import React, { useState, useEffect } from 'react';
import { X, Send, MessageCircle } from 'lucide-react';

const WHATSAPP_NUMBER = '916366979916';
const DEFAULT_MESSAGE = 'Hi CloudXAI, I\'m interested in your services!';

const WhatsAppButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowButton(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleSend = () => {
    const encoded = encodeURIComponent(message.trim() || DEFAULT_MESSAGE);
    window.open(`https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${encoded}`, '_blank');
    setIsOpen(false);
    setMessage(DEFAULT_MESSAGE);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!showButton) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50" data-testid="whatsapp-widget">
      {/* Chat Popup */}
      {isOpen && (
        <div
          className="absolute bottom-20 right-0 w-[340px] rounded-2xl overflow-hidden shadow-2xl shadow-black/40 animate-in slide-in-from-bottom-4 fade-in duration-300"
          data-testid="whatsapp-chat-popup"
        >
          {/* Header */}
          <div className="bg-[#075E54] px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </div>
              <div>
                <p className="text-white font-semibold text-sm">CloudXAI</p>
                <p className="text-green-200 text-xs">Typically replies within minutes</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/70 hover:text-white transition-colors"
              data-testid="whatsapp-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Chat Body */}
          <div className="bg-[#ECE5DD] p-4 min-h-[120px]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c8c0b8' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}>
            <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[85%] relative">
              <p className="text-[#111] text-sm leading-relaxed">
                Hello! Welcome to <strong>CloudXAI</strong>. How can we help you today? Send us a message and we'll get back to you shortly.
              </p>
              <span className="text-[10px] text-gray-400 float-right mt-1">now</span>
            </div>
          </div>

          {/* Input Area */}
          <div className="bg-[#F0F0F0] px-3 py-3 flex items-end gap-2">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              rows={1}
              className="flex-1 resize-none rounded-full px-4 py-2.5 text-sm bg-white border-none outline-none text-gray-800 placeholder:text-gray-400 max-h-[80px] overflow-auto"
              data-testid="whatsapp-message-input"
            />
            <button
              onClick={handleSend}
              className="w-10 h-10 rounded-full bg-[#075E54] hover:bg-[#064E46] flex items-center justify-center flex-shrink-0 transition-colors"
              data-testid="whatsapp-send-btn"
            >
              <Send className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-[60px] h-[60px] rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 ${
          isOpen
            ? 'bg-[#333] hover:bg-[#444]'
            : 'bg-[#25D366] hover:bg-[#1EBE57] shadow-[#25D366]/40'
        }`}
        aria-label="WhatsApp Chat"
        data-testid="whatsapp-toggle-btn"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
        )}
      </button>

      {/* Tooltip (visible when closed) */}
      {!isOpen && (
        <div className="absolute bottom-[72px] right-0 bg-white text-gray-800 text-xs font-medium px-3 py-1.5 rounded-lg shadow-md whitespace-nowrap animate-in fade-in duration-500 pointer-events-none">
          Chat with us on WhatsApp
          <div className="absolute -bottom-1 right-6 w-2 h-2 bg-white rotate-45"></div>
        </div>
      )}
    </div>
  );
};

export const getWhatsAppLink = (formType, data) => {
  const phone = WHATSAPP_NUMBER;
  let text = '';

  switch (formType) {
    case 'career-consultation':
      text = `Hi CloudXAI! I just submitted a Career Consultation request.\n\nName: ${data.name}\nEmail: ${data.email}\nMobile: ${data.mobile}\nCurrent Status: ${data.currentStatus}\nInterested Domain: ${data.interestedDomain}${data.message ? `\nMessage: ${data.message}` : ''}`;
      break;
    case 'corporate-training':
      text = `Hi CloudXAI! I just submitted a Corporate Training enquiry.\n\nOrganization: ${data.organizationName}\nContact Person: ${data.contactPerson}\nEmail: ${data.email}\nTraining Requirement: ${data.trainingRequirement}\nParticipants: ${data.participants}`;
      break;
    case 'hire-talent':
      text = `Hi CloudXAI! I just submitted a Hiring Requirement.\n\nCompany: ${data.companyName}\nContact: ${data.contactPerson}\nEmail: ${data.email}\nMobile: ${data.mobile}\nRequirement: ${data.hiringRequirement}\nPositions: ${data.positions}`;
      break;
    default:
      text = DEFAULT_MESSAGE;
  }

  return `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(text)}`;
};

export default WhatsAppButton;
